// The opening on phones: the whole screen is grass, with a caption asking for the phone to be turned sideways. Once it is
// (or the visitor chooses to carry on upright), the grass parts from the middle to show the map.

const GREEN = '#4f9d45';
const TONES = ['rgba(249,239,137,0.78)', 'rgba(196,222,120,0.8)', 'rgba(112,176,86,0.9)'];

export const isPhone = (): boolean => matchMedia('(pointer: coarse)').matches && Math.min(innerWidth, innerHeight) < 520;

interface Blade { x: number; y: number; h: number; lean: number; ph: number; tone: number; wd: number }

export function initIntro(): { ready: () => void } {
  if (!isPhone()) return { ready: () => undefined };
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.createElement('div');
  root.className = 'intro';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-label', 'Welcome');
  const cv = document.createElement('canvas');
  const cap = document.createElement('div');
  cap.className = 'intro-cap';
  cap.innerHTML = '<div class="intro-phone" aria-hidden="true"><i></i></div><h1>Turn your phone sideways</h1><p>The plot is drawn for a wide screen.</p>';
  const stay = document.createElement('button');
  stay.type = 'button'; stay.className = 'intro-stay'; stay.textContent = 'Carry on upright';
  cap.appendChild(stay);
  root.append(cv, cap);
  document.body.appendChild(root);
  document.body.classList.add('introing');
  const ctx = cv.getContext('2d')!;

  let w = 0, h = 0, blades: Blade[] = [];
  const build = (): void => {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    w = innerWidth; h = innerHeight;
    cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const n = Math.round((w * h) / 95);
    blades = [];
    let s = 7;
    const r = (): number => { s = (s * 16807) % 2147483647; return s / 2147483647; };
    for (let i = 0; i < n; i++) {
      const y = r() * (h + 40);
      blades.push({ x: r() * (w + 20) - 10, y, h: 26 + 30 * r() + (y / h) * 26, lean: (r() - 0.5) * 16, ph: r() * 6.28, tone: r() < 0.38 ? 0 : r() < 0.62 ? 1 : 2, wd: 1.3 + 1.4 * r() + (y / h) });
    }
    blades.sort((a, b) => a.y - b.y);
  };
  build();

  let state: 'waiting' | 'parting' | 'gone' = 'waiting';
  let t0 = 0, loaded = false, upright = false, shownAt = performance.now(), raf = 0;
  const landscape = (): boolean => innerWidth > innerHeight;
  const ease = (x: number): number => x * x * (3 - 2 * x);

  function draw(now: number): void {
    const t = state === 'parting' ? Math.min(1, (now - t0) / (reduce ? 400 : 1700)) : 0;
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2;
    // the ground: two halves that draw back from the middle
    const gap = ease(t) * (w / 2 + 40);
    ctx.fillStyle = GREEN;
    ctx.fillRect(0, 0, Math.max(0, cx - gap), h);
    ctx.fillRect(cx + gap, 0, Math.max(0, w - cx - gap), h);
    ctx.lineCap = 'round';
    for (let tone = 0; tone < 3; tone++) {
      ctx.strokeStyle = TONES[tone];
      for (const b of blades) {
        if (b.tone !== tone) continue;
        const d = (b.x - cx) / (w / 2); // -1 .. 1
        const local = ease(Math.max(0, Math.min(1, (t - (1 - Math.abs(d)) * 0.28) / 0.72))); // blades near the middle go first
        const dir = d < 0 ? -1 : 1;
        const sway = reduce ? 0 : Math.sin(now / 900 + b.ph + b.y * 0.01) * 5;
        const push = dir * local * (w * 0.62 + 80 * Math.abs(d));
        const lean = b.lean + sway + dir * local * b.h * 0.9;
        const alpha = 1 - Math.max(0, (t - 0.72) / 0.28);
        ctx.globalAlpha = alpha;
        ctx.lineWidth = b.wd;
        ctx.beginPath();
        ctx.moveTo(b.x + push, b.y);
        ctx.quadraticCurveTo(b.x + push + lean * 0.3, b.y - b.h * 0.55, b.x + push + lean, b.y - b.h * (1 - local * 0.35));
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
    cap.style.opacity = String(Math.max(0, 1 - t * 3));
    if (state === 'parting' && t >= 1) finish();
  }

  function finish(): void {
    state = 'gone';
    cancelAnimationFrame(raf);
    root.remove();
    document.body.classList.remove('introing');
  }

  function check(): void {
    if (state !== 'waiting') return;
    const ok = (landscape() || upright) && loaded && performance.now() - shownAt > 900;
    if (ok) { state = 'parting'; t0 = performance.now(); cap.classList.add('go'); }
  }

  const tick = (now: number): void => { check(); draw(now); if (state !== 'gone') raf = requestAnimationFrame(tick); };
  raf = requestAnimationFrame(tick);

  const sync = (): void => { // the caption follows the way the phone is held
    const land = landscape();
    cap.querySelector('h1')!.textContent = land ? 'Welcome' : 'Turn your phone sideways';
    cap.querySelector('p')!.textContent = land ? 'Parting the grass…' : 'The plot is drawn for a wide screen.';
    stay.hidden = land;
    root.classList.toggle('land', land);
  };
  sync();
  addEventListener('resize', () => { if (state === 'waiting') { build(); sync(); } });
  stay.addEventListener('click', () => { upright = true; cap.querySelector('h1')!.textContent = 'Welcome'; stay.hidden = true; });

  return { ready: () => { loaded = true; } };
}
